import { useCallback, useEffect, useState } from "react"
import { AlertCircle, MailCheck, RefreshCw, RotateCcw } from "lucide-react"
import { API_ENDPOINTS } from "../config/api"
import { apiPost, fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"

const statusClass = {
  sent: "bg-green-100 text-green-800",
  queued: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  not_tracked: "bg-gray-100 text-gray-700",
}

export default function ReceiptManagement() {
  const { t, translateApiMessage, formatCurrency } = useLanguage()
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [retrying, setRetrying] = useState(null)

  const loadReceipts = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.ADMIN_RECEIPTS}?limit=100`)
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
      const data = await response.json()
      setReceipts(data.receipts || [])
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchReceipts"))
    } finally {
      setLoading(false)
    }
  }, [translateApiMessage])

  useEffect(() => { loadReceipts() }, [loadReceipts])

  const retry = async (bookingId) => {
    setRetrying(bookingId)
    setError("")
    try {
      await apiPost(API_ENDPOINTS.ADMIN_RECEIPT_RETRY(bookingId))
      await loadReceipts()
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.retryReceipt"))
    } finally {
      setRetrying(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="management-title text-3xl font-bold text-gray-800">{t("receipts.title")}</h1>
          <p className="text-sm text-gray-500 mt-2">{t("receipts.subtitle")}</p>
        </div>
        <button type="button" onClick={loadReceipts} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t("receipts.refresh")}
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700"><AlertCircle className="w-5 h-5" />{error}</div>}
      <div className="bg-white rounded-lg shadow p-6">
        {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" /></div> : receipts.length === 0 ? <div className="py-12 text-center text-gray-500"><MailCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />{t("receipts.empty")}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200"><tr>{[t("receipts.booking"), t("receipts.passenger"), t("receipts.completed"), t("receipts.fare"), t("receipts.status"), t("receipts.lastAttempt"), t("receipts.action")].map((heading) => <th key={heading} className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap">{heading}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-200">{receipts.map((receipt) => {
                const status = receipt.receipt_status || "not_tracked"
                const canRetry = status === "failed" || status === "not_tracked"
                return <tr key={receipt.booking_id} className="hover:bg-gray-50 align-top">
                  <td className="px-4 py-4 font-medium">#{receipt.booking_id}</td>
                  <td className="px-4 py-4"><div className="font-medium text-gray-800">{receipt.user_name || t("common.notAvailable")}</div><div className="text-xs text-gray-500">{receipt.recipient_email || t("receipts.noEmail")}</div></td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-600">{receipt.completed_at ? new Date(receipt.completed_at).toLocaleString() : t("common.notAvailable")}</td>
                  <td className="px-4 py-4 font-medium">{formatCurrency(receipt.final_fare || 0)}</td>
                  <td className="px-4 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass[status] || statusClass.not_tracked}`}>{t(`receipts.statuses.${status}`)}</span>{receipt.error && <div className="mt-2 max-w-xs text-xs text-red-600">{receipt.error}</div>}<div className="text-xs text-gray-500 mt-1">{t("receipts.attempts", { count: receipt.attempts || 0 })}</div></td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-600">{receipt.last_attempt_at ? new Date(receipt.last_attempt_at).toLocaleString() : t("common.notAvailable")}</td>
                  <td className="px-4 py-4">{canRetry && <button type="button" onClick={() => retry(receipt.booking_id)} disabled={retrying === receipt.booking_id} className="inline-flex items-center gap-1 text-amber-700 hover:underline disabled:opacity-50"><RotateCcw className="w-4 h-4" />{retrying === receipt.booking_id ? t("receipts.retrying") : t("receipts.retry")}</button>}</td>
                </tr>
              })}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
